import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { BOOKING_IMAGE_BUCKET, getSupabaseClient } from '../lib/supabase'
import type { UploadedImage } from '../types/booking'

const MAX_FILE_SIZE_MB = 5
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

interface ImageUploadProps {
  value: UploadedImage | null
  onChange: (image: UploadedImage | null) => void
  error?: string
}

const ImageUpload = ({ value, onChange, error }: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [feedback, setFeedback] = useState<string | null>(null)
  const progressInterval = useRef<ReturnType<typeof window.setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        window.clearInterval(progressInterval.current)
      }
    }
  }, [])

  const resetProgress = () => {
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current)
    }
    setProgress(0)
  }

  const simulateProgress = () => {
    resetProgress()
    progressInterval.current = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          if (progressInterval.current) window.clearInterval(progressInterval.current)
          return prev
        }
        return prev + 5
      })
    }, 120)
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFeedback('Only JPG, PNG, and WEBP formats are accepted.')
      event.target.value = ''
      return
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFeedback(`File must be smaller than ${MAX_FILE_SIZE_MB}MB.`)
      event.target.value = ''
      return
    }

    try {
      setUploading(true)
      setFeedback(null)
      setProgress(0)
      simulateProgress()

      const supabase = getSupabaseClient()
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      const filePath = `booking/${new Date().toISOString().split('T')[0]}/${crypto.randomUUID()}.${extension}`

      const { error: uploadError } = await supabase.storage.from(BOOKING_IMAGE_BUCKET).upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      })

      if (uploadError) {
        throw uploadError
      }

      const { data: publicData } = supabase.storage.from(BOOKING_IMAGE_BUCKET).getPublicUrl(filePath)

      const uploadedImage: UploadedImage = {
        path: filePath,
        fileName: file.name,
        url: publicData?.publicUrl ?? '',
      }

      onChange(uploadedImage)
      setProgress(100)
      event.target.value = ''
    } catch (uploadErr) {
      console.error(uploadErr)
      const message =
        uploadErr instanceof Error && uploadErr.message.includes('Supabase credentials')
          ? 'Image uploads are not configured yet. Please add your Supabase credentials to the environment.'
          : 'We were unable to upload your image. Please try again.'
      setFeedback(message)
      onChange(null)
    } finally {
      if (progressInterval.current) window.clearInterval(progressInterval.current)
      setUploading(false)
    }
  }

  const handleReplace = () => {
    onChange(null)
    setFeedback(null)
    setProgress(0)
  }

  return (
    <section aria-labelledby="image-upload-heading" className="superz-widget-panel p-6">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Personal Touch</p>
        <h2 id="image-upload-heading" className="font-display text-3xl text-widget-accent">
          Upload Your Image
        </h2>
        <p className="mt-2 text-sm text-widget-accent/70">
          Please share a photo of your SUPERZ perfume bottle to complete your booking.
        </p>
      </header>

      <label
        className={clsx(
          'relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all duration-200',
          uploading
            ? 'border-widget-secondary/80 border-solid bg-widget-secondary/15 text-widget-secondary'
            : 'border-widget-primary/40 bg-widget-primary/10 text-widget-accent',
          'hover:border-widget-secondary/60 hover:border-solid hover:bg-widget-secondary/10 focus-within:border-widget-secondary focus-within:border-solid focus-within:bg-widget-secondary/15 focus-within:outline-none focus-within:ring-2 focus-within:ring-widget-secondary/30 focus-within:ring-offset-2 focus-within:ring-offset-widget-primary',
          error && 'border-red-400/60 bg-red-400/10 text-red-100',
        )}
      >
        <input
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleFileChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-required="true"
          disabled={uploading}
        />
        <span className="text-lg font-semibold">{value ? 'Replace your photo' : 'Drag & drop or click to upload'}</span>
        <span className="text-sm text-widget-accent/70">JPG, PNG, or WEBP — max {MAX_FILE_SIZE_MB}MB</span>

        {uploading && (
          <div className="mt-4 h-2 w-full rounded-full bg-widget-primary/10">
            <div
              className="h-2 rounded-full bg-widget-secondary transition-[width]"
              style={{ width: `${progress}%` }}
              aria-hidden="true"
            />
          </div>
        )}
      </label>

      {value && (
        <div className="mt-4 flex items-center gap-4 rounded-2xl border border-widget-primary/30 bg-widget-primary/15 p-3">
          <img
            src={value.url}
            alt="Uploaded perfume bottle"
            className="h-20 w-20 rounded-xl object-cover"
          />
          <div className="flex-1">
            <p className="font-medium text-widget-accent">{value.fileName}</p>
            <p className="text-xs text-widget-accent/70">{progress === 100 ? 'Upload complete' : `${progress}% uploaded`}</p>
          </div>
          <button
            type="button"
            onClick={handleReplace}
            className="rounded-full border border-widget-primary/30 bg-widget-primary/15 px-4 py-2 text-sm text-widget-accent transition hover:border-widget-secondary hover:bg-widget-secondary/15 hover:text-widget-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary"
          >
            Re-upload
          </button>
        </div>
      )}

      {(feedback || error) && (
        <p role="alert" className="mt-3 text-sm text-red-200">
          {feedback || error}
        </p>
      )}
    </section>
  )
}

export default ImageUpload

